import { Suspense, lazy, startTransition, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollToSection } from '../hooks';
import Hero from '../components/sections/Hero';
// Everything after the Hero is a separate chunk: they mount after first paint
// anyway (useProgressiveSections), so their code needn't be in the main bundle.
const sectionLoaders = {
  bhalil: () => import('../components/sections/Bhalil'),
  rooms: () => import('../components/sections/Rooms'),
  services: () => import('../components/sections/Services'),
  gallery: () => import('../components/sections/Gallery'),
  nearby: () => import('../components/sections/NearbyPlaces'),
  guides: () => import('../components/sections/Guides'),
  faq: () => import('../components/sections/FAQ'),
  reviews: () => import('../components/sections/Reviews'),
  findus: () => import('../components/sections/FindUs'),
};
const Bhalil = lazy(sectionLoaders.bhalil);
const Rooms = lazy(sectionLoaders.rooms);
const Services = lazy(sectionLoaders.services);
const Gallery = lazy(sectionLoaders.gallery);
const NearbyPlaces = lazy(sectionLoaders.nearby);
const Guides = lazy(sectionLoaders.guides);
const FAQ = lazy(sectionLoaders.faq);
const Reviews = lazy(sectionLoaders.reviews);
const FindUs = lazy(sectionLoaders.findus);
import SEO from '../components/seo/SectionSEO';
import { useLocation } from 'react-router-dom';
import { splitLanguageFromPath } from '../lib/localeRoutes';

const nearbyPlacesForSchema = [
  { name: "Fez Medina (Fes el-Bali) – UNESCO World Heritage Site", google_maps: "https://www.google.com/maps/search/?api=1&query=Fes%20el%20Bali%20Medina%2C%20Fes%2C%20Morocco" },
  { name: "Sefrou – Historic Town & Cherry Festival", google_maps: "https://www.google.com/maps/search/?api=1&query=Sefrou%2C%20Morocco" },
  { name: "Ifrane – Little Switzerland of Morocco", google_maps: "https://www.google.com/maps/search/?api=1&query=Ifrane%2C%20Morocco" },
  { name: "Azrou Cedar Forest – Middle Atlas Nature Reserve", google_maps: "https://www.google.com/maps/search/?api=1&query=Azrou%20Cedar%20Forest%2C%20Morocco" },
  { name: "Meknes Medina – Imperial City (UNESCO)", google_maps: "https://www.google.com/maps/search/?api=1&query=Meknes%20Medina%2C%20Morocco" },
  { name: "Moulay Idriss Zerhoun – Sacred Pilgrimage Town", google_maps: "https://www.google.com/maps/search/?api=1&query=Moulay%20Idriss%20Zerhoun%2C%20Morocco" },
  { name: "Volubilis – Roman Ruins (UNESCO)", google_maps: "https://www.google.com/maps/search/?api=1&query=Volubilis%2C%20Morocco" },
];

const DEFERRED_SECTION_COUNT = 9;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

// Mounts the below-the-fold sections one per task (as low-priority
// transitions) instead of all at once, so first load isn't one long
// main-thread block. Any interaction mounts the rest immediately so in-page
// navigation always finds its target; useScrollToSection already retries
// until a section exists, so /rooms-style deep links still land correctly.
function useProgressiveSections() {
  const [count, setCount] = useState(0);

  // Start every chunk download at once; mounting still happens one by one.
  useEffect(() => {
    Object.values(sectionLoaders).forEach((load) => void load());
  }, []);

  useEffect(() => {
    if (count >= DEFERRED_SECTION_COUNT) return;
    const timer = setTimeout(() => startTransition(() => setCount((c) => c + 1)), 0);
    return () => clearTimeout(timer);
  }, [count]);

  useEffect(() => {
    const mountAll = () => startTransition(() => setCount(DEFERRED_SECTION_COUNT));
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, mountAll, { once: true, passive: true }));
    return () => INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, mountAll));
  }, []);

  return count;
}

// Composes the one-page homepage sections and syncs SEO to current route section.
export default function HomePage() {
  const { t } = useTranslation();
  useScrollToSection();
  const location = useLocation();
  // /de/rooms is still the "rooms" section; the prefix only carries language.
  const section = splitLanguageFromPath(location.pathname).path.slice(1) || 'home';
  const mounted = useProgressiveSections();

  const faqItems = (t('faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>) || [];

  return (
    <>
      <SEO
        section={section}
        faqs={faqItems.length > 0 ? faqItems : undefined}
        nearbyPlaces={section === 'nearby' ? nearbyPlacesForSchema : undefined}
      />
      <Hero />
      {/* Reserve a screen of space while sections are still mounting, so the
          footer is never inside the first viewport (it would jump down as each
          section mounts — a large layout shift). One Suspense boundary for all
          deferred sections keeps them in page order. */}
      <div style={{ minHeight: mounted < DEFERRED_SECTION_COUNT ? '100vh' : undefined }}>
      <Suspense fallback={null}>
        {mounted > 0 && <Bhalil />}
        {mounted > 1 && <Rooms />}
        {mounted > 2 && <Services />}
        {mounted > 3 && <Gallery />}
        {mounted > 4 && <NearbyPlaces />}
        {mounted > 5 && <Guides />}
        {mounted > 6 && <FAQ faqs={faqItems} />}
        {mounted > 7 && <Reviews />}
        {mounted > 8 && <FindUs />}
      </Suspense>
      </div>
    </>
  );
}
